import PropTypes from "prop-types";
import { Helmet } from "react-helmet-async";
import { Link as RouterLink } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { get } from "src/utils/networkUtils";

const PAGE_SIZE = 25;
const DAY_OPTIONS = [7, 30, 90, 180];

const VIEW_COPY = {
  overview: {
    title: "User Activity",
  },
  total: {
    title: "Total / Active User Stats",
  },
  registrations: {
    title: "Daily Registration Tracking",
  },
  engagement: {
    title: "Engagement / Retention Report",
  },
  audit: {
    title: "Admin Activity Audit Log",
  },
};

function formatNumber(value) {
  if (value === null || value === undefined || value === "") return "-";
  return new Intl.NumberFormat("en-GB", { maximumFractionDigits: 1 }).format(
    Number(value)
  );
}

function formatDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString();
}

function formatPayload(value) {
  if (!value || (typeof value === "object" && Object.keys(value).length === 0))
    return "-";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch (_err) {
    return "-";
  }
}

function queryString(params) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, String(value));
    }
  });
  return search.toString();
}

function MetricCard({ label, value }) {
  return (
    <Grid item xs={12} sm={6} md={3}>
      <Card sx={{ height: "100%" }}>
        <CardContent>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="h4">{formatNumber(value ?? 0)}</Typography>
        </CardContent>
      </Card>
    </Grid>
  );
}

MetricCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

function LoadingBlock() {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
      <CircularProgress size={28} />
    </Box>
  );
}

function TableCard({ title, children, sx }) {
  return (
    <Card sx={{ width: 1, height: "100%", ...sx }}>
      <Box sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: "divider" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      {children}
    </Card>
  );
}

TableCard.propTypes = {
  children: PropTypes.node.isRequired,
  sx: PropTypes.object,
  title: PropTypes.node.isRequired,
};

TableCard.defaultProps = {
  sx: {},
};

function DaySelect({ days, onChange }) {
  return (
    <TextField
      select
      label="Window"
      value={days}
      onChange={(event) => onChange(Number(event.target.value))}
      sx={{ minWidth: 160 }}
    >
      {DAY_OPTIONS.map((option) => (
        <MenuItem key={option} value={option}>
          Last {option} days
        </MenuItem>
      ))}
    </TextField>
  );
}

DaySelect.propTypes = {
  days: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

function BarCell({ value, max }) {
  const width = max > 0 ? Math.max(4, Math.round((value / max) * 100)) : 0;
  return (
    <Box sx={{ minWidth: 120 }}>
      <Box
        sx={{
          height: 8,
          bgcolor: "divider",
          borderRadius: 0.5,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{ width: `${width}%`, height: "100%", bgcolor: "primary.main" }}
        />
      </Box>
    </Box>
  );
}

BarCell.propTypes = {
  value: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
};

function ActivityTypeTable({ rows }) {
  return (
    <TableCard title="Activity Types">
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell align="right">Events</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(rows ?? []).map((row) => (
              <TableRow key={row.type}>
                <TableCell>
                  <Chip size="small" label={row.type} />
                </TableCell>
                <TableCell align="right">{formatNumber(row.count)}</TableCell>
              </TableRow>
            ))}
            {(rows ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={2} align="center" sx={{ py: 3 }}>
                  No activity events.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </TableCard>
  );
}

ActivityTypeTable.propTypes = {
  rows: PropTypes.array,
};

function useSummary() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    get("/admin/app-users/activity/summary")
      .then((response) => setSummary(response?.data ?? null))
      .finally(() => setLoading(false));
  }, []);

  return { summary, loading };
}

function SummaryMetrics({ summary }) {
  return (
    <Grid container spacing={2}>
      <MetricCard label="Total users" value={summary?.totalUsers} />
      <MetricCard label="Active today" value={summary?.activeToday} />
      <MetricCard label="Active 7 days" value={summary?.activeLast7Days} />
      <MetricCard label="Active 30 days" value={summary?.activeLast30Days} />
      <MetricCard label="New today" value={summary?.newToday} />
      <MetricCard label="New 7 days" value={summary?.newLast7Days} />
      <MetricCard label="New 30 days" value={summary?.newLast30Days} />
      <MetricCard label="Never logged in" value={summary?.neverLoggedIn} />
      <MetricCard label="Users with teams" value={summary?.usersWithTeams} />
      <MetricCard
        label="Users with transfers"
        value={summary?.usersWithTransfers}
      />
      <MetricCard label="Users in leagues" value={summary?.usersWithLeagues} />
      <MetricCard
        label="Users with boosters"
        value={summary?.usersWithBoosters}
      />
    </Grid>
  );
}

SummaryMetrics.propTypes = {
  summary: PropTypes.object,
};

function OverviewView() {
  const { summary, loading } = useSummary();

  if (loading) return <LoadingBlock />;

  return (
    <Stack spacing={3}>
      <SummaryMetrics summary={summary} />
      <Grid container spacing={2}>
        {[
          ["Total / Active User Stats", "/dashboard/user-activity/total"],
          [
            "Daily Registration Tracking",
            "/dashboard/user-activity/daily-registration",
          ],
          [
            "Engagement / Retention Report",
            "/dashboard/user-activity/engagement",
          ],
          ["Admin Activity Audit Log", "/dashboard/user-activity/audit"],
        ].map(([label, path]) => (
          <Grid item xs={12} md={3} key={path}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="subtitle1">{label}</Typography>
                  <Button component={RouterLink} to={path} variant="outlined">
                    Open
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ActivityTypeTable rows={summary?.topActivityTypes ?? []} />
        </Grid>
      </Grid>
    </Stack>
  );
}

function TotalView() {
  const { summary, loading } = useSummary();

  if (loading) return <LoadingBlock />;

  return (
    <Stack spacing={3}>
      <SummaryMetrics summary={summary} />
      <Grid container spacing={2}>
        <MetricCard
          label="Activity events today"
          value={summary?.activityEventsToday}
        />
        <MetricCard
          label="Admin actions today"
          value={summary?.adminActionsToday}
        />
        <Grid item xs={12}>
          <ActivityTypeTable rows={summary?.topActivityTypes ?? []} />
        </Grid>
      </Grid>
    </Stack>
  );
}

function RegistrationsView() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    get(`/admin/app-users/activity/registrations?days=${days}`)
      .then((response) => setData(response?.data ?? null))
      .finally(() => setLoading(false));
  }, [days]);

  const rows = data?.items ?? [];
  const maxRegistrations = Math.max(...rows.map((row) => row.registrations), 0);
  const maxLogins = Math.max(...rows.map((row) => row.loginUsers), 0);

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="flex-end">
        <DaySelect days={days} onChange={setDays} />
      </Stack>
      {loading ? (
        <LoadingBlock />
      ) : (
        <TableCard title="Daily Registrations">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Registrations</TableCell>
                  <TableCell>Registration Trend</TableCell>
                  <TableCell>Login Users</TableCell>
                  <TableCell>Login Trend</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...rows].reverse().map((row) => (
                  <TableRow key={row.date}>
                    <TableCell>{row.date}</TableCell>
                    <TableCell>{formatNumber(row.registrations)}</TableCell>
                    <TableCell>
                      <BarCell
                        value={row.registrations}
                        max={maxRegistrations}
                      />
                    </TableCell>
                    <TableCell>{formatNumber(row.loginUsers)}</TableCell>
                    <TableCell>
                      <BarCell value={row.loginUsers} max={maxLogins} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TableCard>
      )}
    </Stack>
  );
}

function FunnelTable({ funnel }) {
  const registered = funnel?.registered ?? 0;
  const rows = [
    ["Registered", funnel?.registered ?? 0],
    ["Created team", funnel?.createdTeam ?? 0],
    ["Made transfer", funnel?.madeTransfer ?? 0],
    ["Joined league", funnel?.joinedLeague ?? 0],
    ["Used booster", funnel?.usedBooster ?? 0],
  ];

  return (
    <TableCard title="Engagement Funnel">
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Step</TableCell>
              <TableCell align="right">Users</TableCell>
              <TableCell align="right">Share</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(([label, value]) => (
              <TableRow key={label}>
                <TableCell>{label}</TableCell>
                <TableCell align="right">{formatNumber(value)}</TableCell>
                <TableCell align="right">
                  {registered
                    ? `${formatNumber((value / registered) * 100)}%`
                    : "0%"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </TableCard>
  );
}

FunnelTable.propTypes = {
  funnel: PropTypes.object,
};

function EngagementView() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    get(`/admin/app-users/activity/engagement?days=${days}`)
      .then((response) => setData(response?.data ?? null))
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="flex-end">
        <DaySelect days={days} onChange={setDays} />
      </Stack>
      {loading ? (
        <LoadingBlock />
      ) : (
        <Grid container rowSpacing={3} columnSpacing={2}>
          <MetricCard
            label="Never logged in"
            value={data?.loginBuckets?.never}
          />
          <MetricCard
            label="Active 7 days"
            value={data?.loginBuckets?.activeLast7Days}
          />
          <MetricCard
            label="Active 30 days"
            value={data?.loginBuckets?.activeLast30Days}
          />
          <MetricCard
            label="Inactive 30+ days"
            value={data?.loginBuckets?.inactiveOver30Days}
          />
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <FunnelTable funnel={data?.funnel} />
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <ActivityTypeTable rows={data?.activityTypes ?? []} />
          </Grid>
          <Grid item xs={12}>
            <TableCard title="Retention Cohorts">
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Signup Week</TableCell>
                      <TableCell align="right">Registered</TableCell>
                      <TableCell align="right">Active 7 days</TableCell>
                      <TableCell align="right">7 day share</TableCell>
                      <TableCell align="right">Active 30 days</TableCell>
                      <TableCell align="right">30 day share</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data?.retentionCohorts ?? []).map((row) => (
                      <TableRow key={row.week}>
                        <TableCell>{row.week}</TableCell>
                        <TableCell align="right">
                          {formatNumber(row.registered)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(row.activeLast7Days)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(row.activeLast7Pct)}%
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(row.activeLast30Days)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(row.activeLast30Pct)}%
                        </TableCell>
                      </TableRow>
                    ))}
                    {(data?.retentionCohorts ?? []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                          No retention cohort data.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </TableCard>
          </Grid>
        </Grid>
      )}
    </Stack>
  );
}

function AuditView() {
  const [filters, setFilters] = useState({ action: "", entityType: "" });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);

  const filterKey = useMemo(() => JSON.stringify(filters), [filters]);

  useEffect(() => {
    setItems([]);
    setOffset(0);
  }, [filterKey]);

  const load = useCallback(
    (nextOffset) => {
      setLoading(true);
      const params = queryString({
        limit: PAGE_SIZE,
        offset: nextOffset,
        action: filters.action.trim(),
        entityType: filters.entityType.trim(),
      });
      get(`/admin/app-users/activity/audit-log?${params}`)
        .then((response) => {
          const payload = response?.data ?? {};
          const nextItems = payload.items ?? [];
          setItems((prev) =>
            nextOffset === 0 ? nextItems : [...prev, ...nextItems]
          );
          setTotal(payload.total ?? 0);
        })
        .finally(() => setLoading(false));
    },
    [filters]
  );

  useEffect(() => {
    load(0);
  }, [load]);

  const loadMore = () => {
    const nextOffset = offset + PAGE_SIZE;
    setOffset(nextOffset);
    load(nextOffset);
  };

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <TextField
          label="Action"
          value={filters.action}
          onChange={(event) =>
            setFilters((prev) => ({ ...prev, action: event.target.value }))
          }
          sx={{ minWidth: 220 }}
        />
        <TextField
          label="Entity type"
          value={filters.entityType}
          onChange={(event) =>
            setFilters((prev) => ({ ...prev, entityType: event.target.value }))
          }
          sx={{ minWidth: 180 }}
        />
      </Stack>

      <TableCard title={`Audit Log (${formatNumber(total)})`}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Created</TableCell>
                <TableCell>Admin</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Entity</TableCell>
                <TableCell>Payload</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{formatDate(item.createdAt)}</TableCell>
                  <TableCell>
                    {item.adminName || item.adminEmail || "-"}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={item.action} />
                  </TableCell>
                  <TableCell>
                    {item.entityType || "-"}
                    {item.entityId ? ` / ${item.entityId}` : ""}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 420, wordBreak: "break-word" }}>
                    {formatPayload(item.payload)}
                  </TableCell>
                </TableRow>
              ))}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    No audit records.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {loading && <LoadingBlock />}
        {items.length < total && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <Button variant="outlined" disabled={loading} onClick={loadMore}>
              Load more
            </Button>
          </Box>
        )}
      </TableCard>
    </Stack>
  );
}

export default function UserActivityPage({ view }) {
  const copy = VIEW_COPY[view] ?? VIEW_COPY.overview;

  return (
    <>
      <Helmet>
        <title>{copy.title} | DreamTeam Admin</title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Typography variant="h4">{copy.title}</Typography>
          {view === "overview" && <OverviewView />}
          {view === "total" && <TotalView />}
          {view === "registrations" && <RegistrationsView />}
          {view === "engagement" && <EngagementView />}
          {view === "audit" && <AuditView />}
        </Stack>
      </Container>
    </>
  );
}

UserActivityPage.propTypes = {
  view: PropTypes.oneOf([
    "overview",
    "total",
    "registrations",
    "engagement",
    "audit",
  ]),
};

UserActivityPage.defaultProps = {
  view: "overview",
};
